import React, { useState, useEffect } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { shipmentMissingService } from '../../services/shipmentMissingService';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { Plus, AlertTriangle, RefreshCw } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass, listPageWrapperClass } from '../common/uiClasses.js';
import { formatIndianDate, formatIndianTime, formatTimeAgo } from '../../utils/localDate.js';
import { formatAuditUser } from '../../utils/userDisplay.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';


const MissingList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  useEffect(() => {
    fetchMissing();
  }, [page, statusFilter, search]);

  const fetchMissing = async () => {
    try {
      setLoading(true);
      const filters = {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(search ? { search } : {}),
      };
      const response = await shipmentMissingService.getAllMissing(page, 10, filters);
      
      const missingData = response.data.missing || response.data.data || [];
      setMissing((prev) => mergeLoadedPage(page, missingData, prev));
      
      const pagination = response.data.pagination || {};
      setTotalPages(pagination.pages || 1);
    } catch (error) {
      console.error('Error fetching missing:', error);
      Alert.error(error.response?.data?.error || 'Failed to fetch missing shipments');
      setMissing([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'approved': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const hasActiveFilters = statusFilter || search;

  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
    setPage(1);
  };

  const toggleMenu = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const rowActions = (m, menuId) => (
    <RowActionsMenu
      className="relative menu-container"
      isOpen={openMenuId === menuId}
      onToggle={() => toggleMenu(menuId)}
      onClose={() => setOpenMenuId(null)}
      onView={() => navigate(`/missing-shipments/view/${m.missing_id}`)}
      onEdit={
        m.approval_status === 'pending'
          ? () => navigate(`/missing-shipments/edit/${m.missing_id}`)
          : undefined
      }
      editLabel="Review"
    />
  );

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader title="Missing Shipments" icon={AlertTriangle}>
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/missing-shipments/add')}>
            Report Missing
          </Button>
        </ListPageHeader>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
            <SearchSubmitInput
              placeholder="Missing ID, shipment ID..."
              value={search}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
              inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="flex items-end">
            <Button variant="reset" icon={RefreshCw} onClick={fetchMissing} fullWidth>
              Refresh
            </Button>
          </div>
        </ListFilterPanel>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 w-full min-w-0 max-w-full">
          {loading && page === 1 ? (
            <div className="flex items-center justify-center py-20">
              <Loader fullScreen={false} size="default" message="Loading missing shipments..." />
            </div>
          ) : missing.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔭</div>
              <p className="text-gray-600 text-lg font-semibold">No missing shipments found</p>
              <p className="text-gray-500 mt-2">Try adjusting your filters or report a missing item</p>
              <Button variant="primary" onClick={() => navigate('/missing-shipments/add')} className="mt-4">
                Report Missing Item
              </Button>
            </div>
          ) : (
            <>
              <div className="hidden xl:block w-full min-w-0">
                <table className="w-full table-fixed border-collapse [&_th]:min-w-0 [&_td]:min-w-0">
                  <thead className="bg-gray-50 rounded-t-xl">
                    <tr className="border-b border-gray-200">
                      <th className="w-[16%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Missing ID</th>
                      <th className="w-[16%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Shipment ID</th>
                      <th className="w-[24%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Reason</th>
                      <th className="w-[10%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                      <th className="w-[14%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.createdBy')}</th>
                      <th className="w-[12%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                      <th className="w-[8%] px-3 2xl:px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {missing.map((m, index) => (
                      <tr
                        key={m.id}
                        className={`hover:bg-gray-50 relative transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <span
                            onClick={() => navigate(`/missing-shipments/view/${m.missing_id}`)}
                            title={m.missing_id}
                            className={`${listIdLinkClass} break-words`}
                          >
                            {m.missing_id}
                          </span>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <span
                            onClick={() => navigate(`/shipments/view/${m.shipment_id}`)}
                            title={m.shipment_id}
                            className={`${listIdLinkClass} break-words`}
                          >
                            {m.shipment_id}
                          </span>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <div className="font-regular text-gray-900 whitespace-normal break-words" title={m.reason}>
                            {m.reason?.substring(0, 50)}{m.reason?.length > 50 ? '...' : ''}
                          </div>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold uppercase border ${getStatusBadgeColor(m.approval_status)}`}>
                            {m.approval_status}
                          </span>
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top text-gray-900 whitespace-normal break-words">
                          {formatAuditUser(m, 'created_by')}
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm align-top text-gray-900 leading-5">
                          <div className="whitespace-nowrap">{formatIndianDate(m.created_at)}</div>
                          <div className="whitespace-nowrap">{formatIndianTime(m.created_at)}</div>
                          {formatTimeAgo(m.created_at) ? (
                            <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">{formatTimeAgo(m.created_at)}</div>
                          ) : null}
                        </td>
                        <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top">
                          {rowActions(m, m.missing_id)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-3 p-4">
                {missing.map((m) => (
                  <div key={`card-${m.id}`} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-blue-500">
                    <div className="mb-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <span
                            onClick={() => navigate(`/missing-shipments/view/${m.missing_id}`)}
                            title={`#${m.missing_id}`}
                            className={`${listIdLinkMobileClass} block truncate`}
                          >
                            #{m.missing_id}
                          </span>
                          <span
                            onClick={() => navigate(`/shipments/view/${m.shipment_id}`)}
                            className={`${listIdLinkMobileClass} block mt-0.5`}
                          >
                            Shipment: {m.shipment_id}
                          </span>
                          <p className="text-xs text-gray-400 mt-0.5 leading-4">
                            {formatIndianDate(m.created_at)}
                            {formatIndianTime(m.created_at) ? ` · ${formatIndianTime(m.created_at)}` : ''}
                            {formatTimeAgo(m.created_at) ? ` · ${formatTimeAgo(m.created_at)}` : ''}
                          </p>
                        </div>
                        {rowActions(m, `card-${m.missing_id}`)}
                      </div>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getStatusBadgeColor(m.approval_status)}`}>
                        {m.approval_status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="col-span-2 min-w-0">
                        <p className="text-xs text-gray-500 font-medium">Reason</p>
                        <p className="font-semibold text-gray-800 break-words line-clamp-2">{m.reason}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Created By</p>
                        <p className="font-semibold text-gray-800 break-words">{formatAuditUser(m, 'created_by')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Date</p>
                        <p className="font-semibold text-gray-800">{formatIndianDate(m.created_at) || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}

              </div>
              <ListPagination
                currentPage={page}
                totalPages={totalPages}
                loading={loading && page > 1}
                onNext={() => { setLoading(true); setPage((prev) => Math.min(totalPages, prev + 1)); }}
              />
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MissingList;
